import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import type { AppDispatch, RootState } from "../../store/store";
import Dropdown, { DropdownOption } from "../../components/common/Dropdown";
import Loader from "../../components/common/Loader";
import { fetchRegionOptions } from "../../store/slices/selectUBTSlice";

const searchByOptions: DropdownOption[] = [
  { id: "Name", label: "Name", value: "name" },
  { id: "NUID", label: "NUID", value: "nuid" },
];

const SELECT_REGION: DropdownOption = { id: "", label: "Select", value: "" };

interface AddAccessFormProps {
  onSearch: (params: {
    region: DropdownOption | null;
    searchBy: DropdownOption;
    fName?: string;
    lName?: string;
    nuid?: string;
  }) => void;
  onReset?: () => void;
}

const AddAccessForm = ({ onSearch, onReset }: AddAccessFormProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const regionOptions = useSelector((state: RootState) => state.selectUBTSlice.regionOptions ?? []);
  const loadingDropdowns = useSelector((state: RootState) => state.selectUBTSlice.loadingDropdowns ?? false);

  // Remap labels to "code name" format (e.g. "02 Northern California") without touching the slice
  const regionDropdownOptions: DropdownOption[] = [
    SELECT_REGION,
    ...regionOptions.map((o: DropdownOption) => ({
      ...o,
      label: `${o.id} ${o.label}`,
    })),
  ];

  // Fetch region options from backend on mount
  useEffect(() => {
    dispatch(fetchRegionOptions());
  }, [dispatch]);

  const regionRef = useRef<HTMLDivElement>(null);
  const nuidRef = useRef<HTMLDivElement>(null);
  const [regionError, setRegionError] = useState(false);
  const [nuidError, setNuidError] = useState(false);
  const [showValidationBanner, setShowValidationBanner] = useState(false);
  const [region, setRegion] = useState<DropdownOption>(SELECT_REGION);
  const [searchBy, setSearchBy] = useState<DropdownOption>(searchByOptions[0]);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [nuid, setNuid] = useState("");
  const [showReset, setShowReset] = useState(false);

  const handleSearch = () => {
    const hasRegionError = region.value === "";
    const hasNuidError = searchBy.value === "nuid" && nuid.trim() === "";
    setRegionError(hasRegionError);
    setNuidError(hasNuidError);
    if (hasRegionError || hasNuidError) {
      setShowValidationBanner(true);
      if (hasRegionError) {
        regionRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      } else {
        nuidRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      }
      return;
    }
    setShowValidationBanner(false);
    const params: any = {
      region,
      searchBy,
    };
    if (searchBy.value === "nuid") {
      params.nuid = nuid;
    } else {
      params.fName = firstName;
      params.lName = lastName;
    }
    onSearch(params);
    setShowReset(true);
  };

  const focusRegion = useCallback(() => {
    setRegionError(true);
    setShowValidationBanner(true);
    regionRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, []);

  const focusNuid = useCallback(() => {
    setNuidError(true);
    setShowValidationBanner(true);
    nuidRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, []);

  const navigate = useNavigate();
  const handleReset = () => {
    setRegion(SELECT_REGION);
    setRegionError(false);
    setNuidError(false);
    setShowValidationBanner(false);
    setSearchBy(searchByOptions[0]);
    setFirstName("");
    setLastName("");
    setNuid("");
    setShowReset(false);
    if (typeof onReset === "function") onReset();
  };

  function handleCancel(
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ): void {
    event.preventDefault();
    navigate(-1);
  }
  return (
    <div className="flex items-start justify-start">
      {loadingDropdowns && <Loader />}
      <div className="flex flex-col items-start gap-[32px] !mt-8 w-[842px] !p-8 rounded-2xl border-1">
        {showValidationBanner && (
          <section
            aria-live="assertive"
            className="ds-notification w-full"
            data-ds-version="1"
            data-ds-theme="vessel"
            data-ds-variant="Error"
          >
            <div className="ds-notification__icon">
              <img src="/assets/images/errorCircleSmall.svg" alt="Error" width={40} height={40} />
            </div>
            <div className="ds-notification__main-icon">
              <div className="ds-notification__header">
                <div className="ds-notification__heading">
                  There are errors that need your attention before submission:
                </div>
              </div>
              <div className="ds-notification__body">
                <p>Some fields contain invalid or missing information. Review the details below and update the highlighted fields to proceed.</p>
                <ul>
                  {regionError && (
                    <li>
                      <button
                        type="button"
                        onClick={focusRegion}
                        className="underline text-base leading-6 text-kp-blue-dark!"
                      >
                        Region is required
                      </button>
                    </li>
                  )}
                  {nuidError && (
                    <li>
                      <button
                        type="button"
                        onClick={focusNuid}
                        className="underline text-base leading-6 text-kp-blue-dark!"
                      >
                        NUID is required
                      </button>
                    </li>
                  )}
                </ul>
              </div>
            </div>
          </section>
        )}
        <h2 className="text-2xl font-bold mb-6">Add User Access</h2>
        <div className="flex flex-col gap-6 mb-6">
          <div className="flex gap-6">
            <div ref={regionRef} className="w-[342px]">
              <div
                className="ds-form w-full"
                data-ds-theme="vessel"
                data-ds-variant="input"
                data-ds-version="1"
              >
                <Dropdown
                  label="Region"
                  options={regionDropdownOptions}
                  selectedValue={region}
                  onSelect={(opt) => {
                    setRegion(opt);
                    if (opt.value !== "") {
                      setRegionError(false);
                      setShowValidationBanner(false);
                    }
                  }}
                  buttonClassName={regionError ? "w-full! min-w-0 pr-10 border-red-800!" : "w-full!"}
                  menuClassName="w-full!"
                  disabled={showReset}
                />
              </div>
              {regionError && (
                <p className="flex items-center gap-1 text-red-800 text-sm mt-1 ml-1">
                  <img src="/assets/images/errorCircleSmall.svg" alt="Error" width={20} height={20} />
                  <span>Region is required</span>
                </p>
              )}
            </div>
            <div
              className="ds-form w-[342px]"
              data-ds-theme="vessel"
              data-ds-variant="input"
              data-ds-version="1"
            >
              <Dropdown
                label="Search by"
                options={searchByOptions}
                selectedValue={searchBy}
                onSelect={(opt) => {
                  setSearchBy(opt);
                  setNuidError(false);
                  if (!regionError) setShowValidationBanner(false);
                }}
                buttonClassName="w-full!"
                menuClassName="w-full!"
                disabled={showReset}
              />
            </div>
          </div>
          {searchBy.value === "nuid" ? (
            <div ref={nuidRef} className="w-[342px]">
              <div
                className="ds-form w-full"
                data-ds-theme="vessel"
                data-ds-variant="input"
                data-ds-version="1"
              >
                <label htmlFor="nuid">NUID</label>
                <div className="w-full relative">
                  <input
                    autoComplete="on"
                    className={`!w-full block${nuidError ? "w-full! min-w-0 pr-10 border-red-800!" : ""}${showReset ? " opacity-50 cursor-not-allowed" : ""}`}
                    id="nuid"
                    name="nuid"
                    type="text"
                    placeholder="NUID"
                    required
                    disabled={showReset}
                    value={nuid}
                    onChange={(e) => {
                      setNuid(e.target.value);
                      if (e.target.value.trim() !== "") {
                        setNuidError(false);
                        if (!regionError) setShowValidationBanner(false);
                      }
                    }}
                  />
                </div>
              </div>
              {nuidError && (
                <p className="flex items-center gap-1 text-red-800 text-sm mt-1 ml-1">
                  <img src="/assets/images/errorCircleSmall.svg" alt="Error" width={20} height={20} />
                  <span>NUID is required</span>
                </p>
              )}
            </div>
          ) : (
            <div className="flex gap-6">
              <div
                className="ds-form w-[342px]"
                data-ds-theme="vessel"
                data-ds-variant="input"
                data-ds-version="1"
              >
                <label htmlFor="firstName">First name</label>
                <div className="w-full relative">
                  <input
                    autoComplete="on"
                    className={`!w-full block${showReset ? " opacity-50 cursor-not-allowed" : ""}`}
                    id="firstName"
                    name="firstName"
                    type="text"
                    placeholder="First name"
                    required
                    disabled={showReset}
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                  />
                </div>
              </div>
              <div
                className="ds-form w-[342px]"
                data-ds-theme="vessel"
                data-ds-variant="input"
                data-ds-version="1"
              >
                <label htmlFor="lastName">Last name</label>
                <div className="w-full relative">
                  <input
                    autoComplete="on"
                    className={`!w-full block${showReset ? " opacity-50 cursor-not-allowed" : ""}`}
                    id="lastName"
                    name="lastName"
                    type="text"
                    placeholder="Last name"
                    required
                    disabled={showReset}
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
        <div className="flex flex-col-reverse gap-4 min-[992px]:gap-3 min-[992px]:flex-row mt-12 w-full">
          {showReset ? (
            <button
              type="button"
              className="button -light block w-full min-[992px]:w-auto self-stretch flex items-center justify-center box-border max-w-none h-10"
              onClick={handleReset}
            >
              <span className="flex items-center justify-center w-full">
                Reset Search
              </span>
            </button>
          ) : (
            <>
              <button
                type="button"
                className="button -light m-0 block w-full min-[992px]:w-auto self-stretch flex items-center justify-center box-border max-w-none h-10"
                onClick={handleCancel}
              >
                <span className="flex items-center justify-center w-full">
                  Cancel
                </span>
              </button>
              <button
                type="button"
                className="button -primary -light block w-full min-[992px]:w-auto h-10 m-0 self-stretch flex items-center justify-center box-border max-w-none"
                onClick={handleSearch}
              >
                <span className="flex items-center justify-center w-full">
                  Search
                </span>
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AddAccessForm;
